import { Spin } from 'antd';
import { useEffect, useState } from 'react';

import type { Database_Procs_GetGoalsIndex_Item } from '@suntex.fim/api-client';
import { useFramework } from '@suntex.fim/framework';

import { GoalsIndexChartView } from './GoalsIndexChartView';
import type { GoalsIndexChartProps } from './types';

/**
 * Goals Index chart component that fetches its own data.
 * Renders a loading spinner while data is being fetched.
 */
export function GoalsIndexChart({
  id,
  level,
  year,
  phase,
  grade,
  ...chartProps
}: GoalsIndexChartProps): JSX.Element {
  const { api } = useFramework();
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState<Database_Procs_GetGoalsIndex_Item | null>(
    null,
  );

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      setIsLoading(true);
      try {
        const response = await api.get_Assessment_GoalsIndex({
          id,
          level,
          year,
          phase,
          grade,
        });
        if (!cancelled) {
          setData(response.data);
        }
      } catch {
        if (!cancelled) {
          setData(null);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    void fetchData();

    return () => {
      cancelled = true;
    };
  }, [api, id, level, year, phase, grade]);

  if (isLoading) {
    return <Spin />;
  }

  if (!data) {
    return <div>No data available</div>;
  }

  return <GoalsIndexChartView data={data} phase={phase} {...chartProps} />;
}
